import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { apiService } from '../../services/api';
import { getImageUrlSync, getApiBaseURL } from '../../utils/imageProxy';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ProductImage {
  url: string;
  storageId?: string;
  alt?: string;
  isPrimary: boolean;
  order: number;
}

export interface ProductImageUploadProps {
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
  maxImages?: number;
  label?: string;
  error?: string;
  containerStyle?: any;
  productId?: string; // Product ID for uploading images (required for upload)
}

export const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  images = [],
  onChange,
  maxImages = 10,
  label,
  error,
  containerStyle,
  productId,
}) => {
  const styles = useThemedStyles(getStyles);
  const [uploading, setUploading] = useState(false);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);

  // Helper function to get image URL with proxy support
  const getImageUrl = useCallback((url: string): string => {
    return getImageUrlSync(url, apiBaseURL);
  }, [apiBaseURL]);

  const requestPermissions = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      return true; // Web doesn't need permissions
    }

    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert(
        'Permission Required',
        'Please grant permission to access your photo library to upload images.',
        [{ text: 'OK' }]
      );
      return false;
    }
    return true;
  };

  const pickImage = async () => {
    if (images.length >= maxImages) {
      Alert.alert('Limit Reached', `You can only upload up to ${maxImages} images.`);
      return;
    }

    const hasPermission = await requestPermissions();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        if (!productId) {
          // If no product ID, we're probably creating a new product
          // Store local URIs for now, they'll be uploaded when the product is created
          const newImages = result.assets.map((asset, index) => ({
            url: asset.uri,
            isPrimary: images.length === 0 && index === 0,
            order: images.length + index,
          }));
          onChange([...images, ...newImages]);
        } else {
          // Upload each image to storage
          setUploading(true);
          const uploadPromises = result.assets.map(async (asset, index) => {
            try {
              const fileName = asset.fileName || `image_${Date.now()}_${index}.jpg`;
              const fileType = asset.type || 'image/jpeg';
              
              const uploadResult = await apiService.uploadProductImage(
                productId,
                asset.uri,
                fileName,
                fileType
              );

              if (uploadResult.statusCode === 1000 && uploadResult.data) {
                return {
                  url: uploadResult.data.url,
                  alt: uploadResult.data.alt || fileName,
                  isPrimary: images.length === 0 && index === 0,
                  order: images.length + index,
                };
              } else {
                throw new Error(uploadResult.errors?.[0]?.message || 'Failed to upload image');
              }
            } catch (error: any) {
              console.error('Error uploading image:', error);
              Alert.alert('Upload Error', `Failed to upload image: ${error.message}`);
              // Return local URI as fallback
              return {
                url: asset.uri,
                isPrimary: images.length === 0 && index === 0,
                order: images.length + index,
              };
            }
          });

          const uploadedImages = await Promise.all(uploadPromises);
          onChange([...images, ...uploadedImages]);
          setUploading(false);
        }
      }
    } catch (error: any) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    
    // If we removed the primary image, make the first remaining image primary
    if (images[index].isPrimary && newImages.length > 0) {
      newImages[0].isPrimary = true;
    }
    
    // Reorder images
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      order: i,
    }));
    
    onChange(reorderedImages);
  };

  const setPrimaryImage = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    onChange(newImages);
  };

  const reorderImages = (fromIndex: number, toIndex: number) => {
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    
    // Reorder
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      order: i,
    }));
    
    onChange(reorderedImages);
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && <Text style={styles.label}>{label}</Text>}
      
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Existing Images */}
        {images.map((image, index) => {
          const imageUrl = getImageUrl(image.url);
          return (
          <View key={index} style={styles.imageWrapper}>
            <Image source={{ uri: imageUrl }} style={styles.image} />
            
            {/* Primary Badge */}
            {image.isPrimary && (
              <View style={styles.primaryBadge}>
                <Text style={styles.primaryText}>Primary</Text>
              </View>
            )}
            
            {/* Actions Overlay */}
            <View style={styles.imageActions}>
              {!image.isPrimary && (
                <TouchableOpacity
                  style={[styles.imageActionButton, styles.primaryButton]}
                  onPress={() => setPrimaryImage(index)}
                >
                  <Ionicons name="star" size={16} color={colors.white} />
                </TouchableOpacity>
              )}
              
              <TouchableOpacity
                style={[styles.imageActionButton, styles.deleteButton]}
                onPress={() => removeImage(index)}
              >
                <Ionicons name="trash" size={16} color={colors.white} />
              </TouchableOpacity>
            </View>
          </View>
          );
        })}

        {/* Add Image Button */}
        {images.length < maxImages && (
          <TouchableOpacity
            style={styles.addButton}
            onPress={pickImage}
            disabled={uploading}
          >
            {uploading ? (
              <Ionicons name="hourglass-outline" size={32} color={colors.primary} />
            ) : (
              <>
                <Ionicons name="add-circle-outline" size={32} color={colors.primary} />
                <Text style={styles.addButtonText}>Add Image</Text>
              </>
            )}
          </TouchableOpacity>
        )}
      </ScrollView>

      {error && <Text style={styles.errorText}>{error}</Text>}
      
      {images.length > 0 && (
        <Text style={styles.helperText}>
          {images.length} / {maxImages} images. Tap an image to set as primary.
        </Text>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.medium,
  },
  scrollView: {
    marginHorizontal: -spacing.md,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
  },
  imageWrapper: {
    position: 'relative',
    width: 120,
    height: 120,
    borderRadius: designTokens.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: colors.lightGray,
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  primaryBadge: {
    position: 'absolute',
    top: spacing.xs,
    left: spacing.xs,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  primaryText: {
    color: colors.white,
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  imageActions: {
    position: 'absolute',
    bottom: spacing.xs,
    right: spacing.xs,
    flexDirection: 'row',
    gap: spacing.xs,
  },
  imageActionButton: {
    width: 28,
    height: 28,
    borderRadius: designTokens.borderRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: colors.accent,
  },
  deleteButton: {
    backgroundColor: colors.error,
  },
  addButton: {
    width: 120,
    height: 120,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 2,
    borderColor: colors.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
  },
  addButtonText: {
    marginTop: spacing.xs,
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  helperText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
});

